import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUsers } from "@/api/queries/users";
import { useSetSupervisor } from "@/api/mutations/users";
import type { User } from "@/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

const UNSET = "__unset__";

export function SetSupervisorDialog({ open, onOpenChange, user }: Props) {
  const usersQuery = useUsers();
  const mutation = useSetSupervisor();
  const [selected, setSelected] = useState<string>(user.supervisorId ?? UNSET);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(user.supervisorId ?? UNSET);
      setServerError(null);
    }
  }, [open, user.supervisorId]);

  // Self ausfiltern damit der User nicht sich selbst als Supervisor auswählt
  const candidates = (usersQuery.data ?? []).filter((u) => u.id !== user.id);

  function handleSubmit() {
    const supervisorId = selected === UNSET ? null : selected;
    mutation.mutate(
      { id: user.id, supervisorId },
      {
        onSuccess: () => {
          toast.success(supervisorId ? "Supervisor gesetzt" : "Supervisor entfernt");
          onOpenChange(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Set fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="set-supervisor-dialog">
        <DialogHeader>
          <DialogTitle>Supervisor setzen</DialogTitle>
          <DialogDescription>
            Org-Hierarchie. Loops + Self-Reference werden vom Master geprüft.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <Label>Supervisor</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="supervisor-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNSET}>— kein Supervisor</SelectItem>
              {candidates.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.email} ({u.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="supervisor-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="supervisor-cancel">
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            data-testid="supervisor-submit"
          >
            {mutation.isPending ? "..." : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
